/**
 * iter87.466.1 — UCL Advance Predictor pick modal, sibling of
 * `MlsSeriesPickModal.jsx` — simplified to ONE pick per 2-leg tie
 * (who advances the aggregate, not a leg-by-leg winner + game count).
 * Spans FOUR rounds (Knockout Round Play-offs, Round of 16,
 * Quarter-finals, Semi-finals) instead of MLS's single Round One.
 * Scoring: Knockout + Round of 16 = +3/-1, Quarter/Semi-finals = +6/-2.
 *
 * iter87.467 (user-requested — "single elimination playoff
 * predictors act the same way... take the MLB Series Predictor
 * approach") — rebuilt from a Round dropdown (one round visible at a
 * time) onto the same 3-section shape as `MlbSeriesPickModal.jsx`:
 * every tie across all four rounds is visible at once, split into
 * Open — Make Your Pick (always shown), Locked & Live (collapsible,
 * starts open) and Completed (collapsible, starts closed, tinted
 * green/red for hit/miss). This also retires the old "unsaved picks
 * in another round" warning entirely — there's no round switcher to
 * navigate away from anymore, so nothing can be silently lost.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Info, Check, Lock, X, Trophy, ChevronDown } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";

const TIER_BY_ROUND = { ko_playoff: "a", r16: "a", qf: "b", sf: "b" };
const TIER_POINTS = { a: { correct: 3, incorrect: -1 }, b: { correct: 6, incorrect: -2 } };

function SeedChip({ seed }) {
    return (
        <span className="inline-flex w-6 h-6 items-center justify-center rounded-lg bg-black border border-white/10 font-mono text-[10px] font-bold text-zinc-400 shrink-0">
            {seed ?? "–"}
        </span>
    );
}

export default function UclAdvancePickModal({ open, onClose, tournamentId, participantId, teams, existingPicks, onPicksChanged }) {
    const [bracket, setBracket] = useState(null);
    const [picks, setPicks] = useState(existingPicks || {});
    const [savingAll, setSavingAll] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [showLive, setShowLive] = useState(true);
    const [showCompleted, setShowCompleted] = useState(false);

    const teamFor = (id) => (teams || []).find((t) => t.id === id);
    const nameFor = (id) => teamFor(id)?.name || id || "TBD";

    const existingPicksRef = useRef(existingPicks);
    existingPicksRef.current = existingPicks;

    useEffect(() => {
        if (!open || !tournamentId) return;
        const load = () => api.getUclBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
        load();
        setPicks(existingPicksRef.current || {});
        const id = setInterval(load, 45000);
        return () => clearInterval(id);
    }, [open, tournamentId]);

    if (!open) return null;

    const allRounds = (bracket?.rounds || []).filter((r) => r.key !== "final");
    const roundLabel = {};
    allRounds.forEach((r) => { roundLabel[r.key] = r.label; });
    const allTies = allRounds.flatMap((r) =>
        (r.series || []).filter((s) => s.status !== "tbd").map((s) => ({ ...s, _roundKey: r.key })));
    const notSetYet = !bracket || allTies.length === 0;

    const openTies = allTies.filter((s) => s.status === "open" && !s.locked);
    const liveTies = allTies.filter((s) => s.status === "open" && s.locked);
    const completedTies = allTies.filter((s) => s.status === "final");
    const byRound = (list) => list.reduce((acc, s) => {
        if (!acc[s._roundKey]) acc[s._roundKey] = { label: roundLabel[s._roundKey], series: [] };
        acc[s._roundKey].series.push(s);
        return acc;
    }, {});
    const liveByRound = byRound(liveTies);
    const completedByRound = byRound(completedTies);

    const selectPick = (seriesId, teamId) => {
        setPicks((prev) => ({ ...prev, [seriesId]: teamId }));
    };

    const onSavePicks = async () => {
        setSavingAll(true);
        try {
            const toSave = openTies.filter((s) => picks[s.id]);
            await Promise.all(toSave.map((s) =>
                api.submitUclAdvancePick(tournamentId, participantId, s.id, picks[s.id])));
            onPicksChanged?.(picks);
            toast.success("All picks saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Some picks failed to save — try again");
        } finally {
            setSavingAll(false);
        }
    };

    const onClearAll = async () => {
        const idsWithPicks = openTies.filter((s) => picks[s.id]).map((s) => s.id);
        if (idsWithPicks.length === 0) {
            toast.info("No picks to clear yet");
            return;
        }
        setClearing(true);
        try {
            await api.clearUclAdvancePicks(tournamentId, participantId, idsWithPicks);
            const next = { ...picks };
            idsWithPicks.forEach((id) => { delete next[id]; });
            setPicks(next);
            onPicksChanged?.(next);
            toast.success("Picks cleared");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't clear picks");
        } finally {
            setClearing(false);
        }
    };

    const renderSeriesCard = (s) => {
        const pick = picks[s.id];
        const locked = s.status !== "open" || s.locked;
        const isFinal = s.status === "final";
        const tier = TIER_BY_ROUND[s._roundKey] || "a";
        const pickedWinner = isFinal && !!pick && pick === s.advance_team_id;
        const pickedLoser = isFinal && !!pick && pick !== s.advance_team_id;
        const outerBorderClass = pickedWinner ? "border-emerald-500" : pickedLoser ? "border-red-500" : "border-neutral-800";
        return (
            <div
                key={s.id}
                data-testid={`ucl-advance-${s.id}`}
                data-result={isFinal ? (pickedWinner ? "correct" : pickedLoser ? "incorrect" : "no-pick") : undefined}
                className={`brutal-card bg-neutral-900 border-2 ${outerBorderClass} p-4 space-y-3`}
            >
                <div className="flex items-center gap-2 flex-wrap">
                    {locked && !isFinal && <Lock className="w-3 h-3 text-zinc-500 shrink-0" data-testid={`ucl-advance-${s.id}-locked-icon`} />}
                    <span className="inline-flex items-center rounded-lg bg-neutral-950 border border-white/10 text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider text-zinc-400">
                        {roundLabel[s._roundKey]}
                    </span>
                    <span className="ml-auto text-[9px] font-mono text-zinc-500">
                        +{TIER_POINTS[tier].correct}/{TIER_POINTS[tier].incorrect}
                    </span>
                </div>
                {isFinal && (
                    <p data-testid={`ucl-advance-${s.id}-result`} className="text-[10px] uppercase tracking-widest text-zinc-400">
                        Final — <span className="text-cyber font-bold">{nameFor(s.advance_team_id)}</span> advances
                    </p>
                )}
                <div>
                    {[[s.team_a_id, s.team_a_seed], [s.team_b_id, s.team_b_seed]].map(([tid, seed], idx) => {
                        const isSelected = pick === tid;
                        let rowClass = isSelected ? "border-volt bg-volt/20" : "border-transparent bg-neutral-800 hover:border-neutral-600";
                        if (isFinal && isSelected) {
                            rowClass = tid === s.advance_team_id ? "border-emerald-500 bg-emerald-500/10" : "border-red-500 bg-red-500/10";
                        }
                        return (
                            <button
                                key={tid || idx}
                                data-testid={`ucl-advance-${s.id}-pick-${idx === 0 ? "a" : "b"}`}
                                aria-pressed={isSelected}
                                disabled={locked || !tid}
                                onClick={() => selectPick(s.id, tid)}
                                className={`flex items-center justify-between w-full p-3 mb-2 rounded-lg border-2 transition-all
                                    ${rowClass}
                                    ${locked && !isFinal ? "opacity-50 cursor-not-allowed" : locked ? "cursor-not-allowed" : ""}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <SeedChip seed={seed} />
                                    <TeamSwatch name={nameFor(tid)} sport="ucl" size="w-6 h-6 text-[9px]" />
                                    <span className={`text-sm font-bold truncate text-left ${isSelected ? (isFinal ? (tid === s.advance_team_id ? "text-emerald-400" : "text-red-400") : "text-volt") : "text-white"}`}>{nameFor(tid)}</span>
                                </div>
                                {isSelected && <Check className={`w-4 h-4 shrink-0 ml-2 ${isFinal ? (tid === s.advance_team_id ? "text-emerald-400" : "text-red-400") : "text-volt"}`} />}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="ucl-advance-pick-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="brutal-card max-w-lg w-full max-h-[90dvh] overflow-y-auto border-volt"
        >
            <div className="sticky top-0 z-10 bg-[#121212] border-b-2 border-neutral-800 px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
                <h3 className="font-display font-extrabold uppercase text-lg flex items-center gap-2">
                    <Trophy className="w-[18px] h-[18px]" />
                    Two-Leg Predictor
                </h3>
                <button
                    onClick={onClose}
                    className="brutal-btn-ghost p-2 shrink-0"
                    data-testid="close-ucl-advance-pick-modal-btn"
                    aria-label="Close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="space-y-4 p-4 sm:p-6">
                <div data-testid="ucl-advance-how-it-works" className="text-neutral-400 text-[11px] pb-4 border-b-2 border-neutral-800 flex gap-2 items-start text-left">
                    <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                    <p className="leading-relaxed">
                        <span className="text-white font-bold uppercase tracking-tight">How it works:</span> pick who advances
                        each 2-leg tie below. Knockout + Round of 16 — correct = <span className="text-cyber font-semibold">+3</span>,
                        incorrect = <span className="text-heat font-semibold">−1</span>. Quarter/Semi-finals — correct = <span className="text-cyber font-semibold">+6</span>,
                        incorrect = <span className="text-heat font-semibold">−2</span>. Each tie locks once its own Leg 1 starts — every round, past and future, stays visible below.
                    </p>
                </div>

                {notSetYet && (
                    <p data-testid="ucl-advance-not-set-notice" className="text-xs text-zinc-500 -mt-1">
                        No ties are set yet — check back once the bracket is drawn. Sections below will fill in as rounds are scheduled.
                    </p>
                )}
                <div className="space-y-4">
                    <div className="space-y-3">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold" data-testid="ucl-advance-open-heading">
                            Open — Make Your Pick ({openTies.length})
                        </p>
                        {openTies.length > 0 ? openTies.map(renderSeriesCard) : (
                            <p data-testid="ucl-advance-no-open-notice" className="text-xs text-zinc-500">
                                No open ties right now — check back once new matchups are set.
                            </p>
                        )}
                    </div>

                        <Collapsible open={showLive} onOpenChange={setShowLive}>
                            <CollapsibleTrigger
                                data-testid="ucl-advance-live-toggle"
                                className="flex items-center justify-between w-full py-2 border-t-2 border-neutral-800 text-left"
                            >
                                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5">
                                    <Lock className="w-3 h-3" /> Locked & Live ({liveTies.length})
                                </span>
                                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showLive ? "rotate-180" : ""}`} />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-4 pt-3" data-testid="ucl-advance-live-content">
                                {liveTies.length > 0 ? (
                                    Object.entries(liveByRound).map(([roundKey, group]) => (
                                        <div key={roundKey} className="space-y-3">
                                            <p className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{group.label}</p>
                                            {group.series.map(renderSeriesCard)}
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-xs text-zinc-500" data-testid="ucl-advance-live-empty-notice">
                                        Nothing locked or in progress right now.
                                    </p>
                                )}
                            </CollapsibleContent>
                        </Collapsible>

                        <Collapsible open={showCompleted} onOpenChange={setShowCompleted}>
                            <CollapsibleTrigger
                                data-testid="ucl-advance-completed-toggle"
                                className="flex items-center justify-between w-full py-2 border-t-2 border-neutral-800 text-left"
                            >
                                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5">
                                    <Trophy className="w-3 h-3" /> Completed ({completedTies.length})
                                </span>
                                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showCompleted ? "rotate-180" : ""}`} />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-4 pt-3" data-testid="ucl-advance-completed-content">
                                {completedTies.length > 0 ? (
                                    Object.entries(completedByRound).map(([roundKey, group]) => (
                                        <div key={roundKey} className="space-y-3">
                                            <p className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{group.label}</p>
                                            {group.series.map(renderSeriesCard)}
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-xs text-zinc-500" data-testid="ucl-advance-completed-empty-notice">
                                        No ties have finished yet.
                                    </p>
                                )}
                            </CollapsibleContent>
                        </Collapsible>
                    </div>
            </div>
            <div className="sticky bottom-0 z-10 mt-4 flex items-center gap-2 bg-[#121212] border-t-2 border-neutral-800 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <button
                        type="button"
                        onClick={onClearAll}
                        disabled={clearing || savingAll}
                        data-testid="ucl-advance-clear-all-btn"
                        className="brutal-btn-ghost flex-1 py-2.5 text-xs font-bold uppercase tracking-widest disabled:opacity-40"
                    >
                        {clearing ? "Clearing…" : "Clear All Picks"}
                    </button>
                    <button
                        type="button"
                        onClick={onSavePicks}
                        disabled={savingAll || clearing || openTies.filter((s) => picks[s.id]).length === 0}
                        data-testid="ucl-advance-save-picks-btn"
                        className="brutal-btn flex-1 py-2.5 text-xs font-bold uppercase tracking-widest bg-volt text-black border-volt disabled:opacity-40"
                    >
                        {savingAll ? "Saving…" : "Save Picks"}
                    </button>
            </div>
        </BentoModal>
    );
}
